/**
 * 解码结果 → 文档（M2-03）。
 *
 * 像素按 256 块切进 TileStore（CLAUDE.md 规则 6），图层只拿 TileHandle。
 * 解码用的整图缓冲在本函数返回后没有任何引用残留——这是「无整图 buffer
 * 滞留」验收的实现侧。
 *
 * 当前限制：16 位输入降到 8 位（TileStore 目前是 RGBA8，文档 bitDepth 记 8）；
 * ICC 原始字节挂到 workingSpace 上传递，不做转换（色彩管理是 M3 的活）。
 */
import { decodedImageToRgba8, type DecodedImage } from '../../io/codecs';
import type { Document, RasterLayer } from '../../document/types';
import { createDocument, layerDefaults } from '../../document/draft';
import { TILE, layerId, tileKey } from '../../engine/tile/key';
import type { TileStore } from '../../engine/tile/store';

export interface ImportOptions {
  readonly id: string;
  readonly fileName: string;
  readonly now: () => string;
}

/** 把一张解码图导入为单图层文档，像素写入 tiles。 */
export function documentFromImage(
  image: DecodedImage,
  tiles: TileStore,
  options: ImportOptions,
): Document {
  const { width, height } = image;
  const rgba8 = decodedImageToRgba8(image);

  const id = layerId(`${options.id}:bg`);
  const tilesX = Math.ceil(width / TILE);
  const tilesY = Math.ceil(height / TILE);

  const buffer = new Uint8Array(TILE * TILE * 4);
  for (let ty = 0; ty < tilesY; ty++) {
    for (let tx = 0; tx < tilesX; tx++) {
      buffer.fill(0);
      const originX = tx * TILE;
      const originY = ty * TILE;
      const copyW = Math.min(TILE, width - originX);
      const copyH = Math.min(TILE, height - originY);
      for (let y = 0; y < copyH; y++) {
        const src = ((originY + y) * width + originX) * 4;
        buffer.set(rgba8.subarray(src, src + copyW * 4), y * TILE * 4);
      }
      // set() 内部会复制，buffer 可以复用——避免为 8K 图分配上千个临时块
      tiles.set(tileKey(id, tx, ty), buffer);
    }
  }

  const layer: RasterLayer = {
    ...layerDefaults(id, '背景'),
    kind: 'raster',
    tiles: tiles.handle(id),
    bounds: { x: 0, y: 0, width, height },
  };

  // 组装全新文档而不是走 DocumentDraft：draft 是「修改现有文档」的通道，
  // 归命令层专用（test/app/command-bus.test.ts 的扫描守着）。
  // 这里是从零构造，直接给出只读对象即可。
  const base = createDocument({
    id: options.id,
    width,
    height,
    title: options.fileName,
    createdAt: options.now(),
  });
  return {
    ...base,
    workingSpace:
      image.icc === null
        ? base.workingSpace
        : { ...base.workingSpace, iccBytes: image.icc },
    meta: { ...base.meta, sourceFileName: options.fileName },
    root: { ...base.root, children: [layer] },
  };
}
